import React, { useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { ButtonStandard, Link, Pagination, Skeleton, Table, Text } from "@performyard/py-components";
import type { PYColumnDef, SortChangedEvent } from "@performyard/py-components";
import { SignatureRequestTemplateSortBySchema } from "@performyard/service-contracts";
import type { SignatureRequestTemplateListItem } from "@performyard/service-contracts";

import { AdminTitle } from "../../../../components/Admin";
import Spacer from "../../../../components/Spacer";
import SignatureRequestDocumentPicker from "./SignatureRequestDocumentPicker";

import { useSignatureRequestTemplates } from "../../../../api/hooks/defs/signature-request-templates";

import momentize from "../../../../utils/Momentize";

import useInfoPageTemplateQueryState from "../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";

const SIGNATURE_REQUEST_TEMPLATES_PAGE_SIZE = 25;

const isLoadingRow = (data: Partial<SignatureRequestTemplateListItem>) => !data._id;

const SignatureRequestsTab = () => {
	const [isPickerOpen, setIsPickerOpen] = useState(false);

	const { sort, setSort, pageNumber, setPageNumber } = useInfoPageTemplateQueryState({
		defaultSortName: "name",
		defaultSortDirection: "asc",
		defaultPageNumber: 1,
	});

	const parsedSortBy = SignatureRequestTemplateSortBySchema.safeParse(sort.name);
	const sortBy = parsedSortBy.success ? parsedSortBy.data : "name";

	const { data, isLoading } = useSignatureRequestTemplates(
		{
			sortBy,
			sortOrder: sort.direction,
			limit: SIGNATURE_REQUEST_TEMPLATES_PAGE_SIZE,
			skip: (pageNumber - 1) * SIGNATURE_REQUEST_TEMPLATES_PAGE_SIZE,
		},
		{ placeholderData: (previousData) => previousData }
	);

	const templates = data?.signatureRequestTemplates ?? [];
	const totalCount = data?.totalCount ?? 0;
	const pageCount = Math.ceil(totalCount / SIGNATURE_REQUEST_TEMPLATES_PAGE_SIZE);

	const onSortChanged = (event: SortChangedEvent<SignatureRequestTemplateListItem>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			setSort({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	const columns: PYColumnDef<SignatureRequestTemplateListItem>[] = [
		{
			colId: "name",
			headerName: "Signature Request",
			flex: 1,
			sortable: true,
			cellRenderer: (params: { data: SignatureRequestTemplateListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				return (
					<div className="flex h-full w-full min-w-0 items-center">
						<Link href={`/doc-signing/templates/${row._id}/edit/`}>
							<Text
								size="paragraph-3"
								weight="regular"
								dangerouslySetClassName="py-line-clamp-1 w-full min-w-0 max-w-full"
							>
								{row.name}
							</Text>
						</Link>
					</div>
				);
			},
		},
		{
			colId: "last_update",
			field: "lastUpdate",
			headerName: "Last Update",
			width: 220,
			sortable: true,
			cellRenderer: (params: { data: SignatureRequestTemplateListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-24" />;
				if (!row.lastUpdate) return null;
				return momentize(row.lastUpdate).calendar();
			},
		},
		{
			colId: "actorCount",
			headerName: "Actors",
			width: 140,
			sortable: true,
			headerClass: "[&_.ag-header-cell-label]:justify-center",
			cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
			valueGetter: (params: { data?: SignatureRequestTemplateListItem }) => params.data?.actorCount ?? 0,
			cellRenderer: (params: { data: SignatureRequestTemplateListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-6" />;
				return row.actorCount;
			},
		},
	];

	return (
		<div>
			<div className="flex items-center justify-between gap-4">
				<AdminTitle includeSpacer={false}>Signature Request Templates</AdminTitle>
				<div>
					<ButtonStandard
						color="primary"
						iconLeft={faPlus}
						dangerouslySetClassName="w-auto"
						onClick={() => setIsPickerOpen(true)}
					>
						Create New Signature Request Template
					</ButtonStandard>
					<Spacer height={2} />
				</div>
			</div>

			<div>
				<Table<SignatureRequestTemplateListItem>
					autoHeight
					columnSizing="fit-grid"
					columnDefs={columns}
					rowData={templates}
					loading={isLoading}
					getRowId={(params) => params.data._id}
					rowHeight={48}
					defaultColDef={{
						cellStyle: { display: "flex", alignItems: "center" },
						resizable: false,
						sortingOrder: ["asc", "desc"],
					}}
					emptyStateText="No Signature Request Templates Found"
					onSortChanged={onSortChanged}
					initialState={{
						sort: {
							sortModel: sort.name ? [{ colId: sort.name, sort: sort.direction }] : [],
						},
					}}
				/>
			</div>

			{pageCount > 1 && (
				<div className="mt-4 flex justify-center">
					<Pagination currentPage={pageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			)}

			<SignatureRequestDocumentPicker isOpen={isPickerOpen} onClose={() => setIsPickerOpen(false)} />
		</div>
	);
};

export default SignatureRequestsTab;
